import { useEffect, useMemo, useState } from "react";
import {
  listHelpTerms, createHelpTerm, updateHelpTerm, deleteHelpTerm,
} from "../lib/adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Plus, Search, Pencil, Trash2, X } from "lucide-react";

const CATEGORIES = [
  "Org", "Inbound", "Outbound", "Inventory",
  "Automation", "Integration", "Reporting", "PainPoints",
];

const ASSESSMENT_TYPES = ["Warehouse", "Cloud Migration", "Transportation", "Common"];

const EMPTY = {
  term: "",
  keywords: "",
  definition: "",
  why_it_matters: "",
  example: "",
  category: "Org",
  assessment_type: "Warehouse",
  related_question_ids: "",
  active: true,
};

export default function HelpKnowledgePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [atFilter, setAtFilter] = useState("all");

  // Modal state
  const [editing, setEditing] = useState(null); // null | id | "new"
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    setLoading(true);
    return listHelpTerms()
      .then(setItems)
      .catch(() => setErr("Failed to load help knowledge"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((t) => {
      if (catFilter !== "all" && t.category !== catFilter) return false;
      if (atFilter !== "all" && (t.assessment_type || "Warehouse") !== atFilter) return false;
      if (!q) return true;
      const hay = [t.term, ...(t.keywords || []), ...(t.related_question_ids || [])].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, catFilter, atFilter]);

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };

  const openEdit = (t) => {
    setForm({
      term: t.term || "",
      keywords: (t.keywords || []).join(", "),
      definition: t.definition || "",
      why_it_matters: t.why_it_matters || "",
      example: t.example || "",
      category: t.category || "Org",
      assessment_type: t.assessment_type || "Warehouse",
      related_question_ids: (t.related_question_ids || []).join(", "),
      active: t.active !== false,
    });
    setEditing(t.id);
  };

  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.term.trim() || !form.definition.trim()) {
      setErr("Term and Definition are required");
      return;
    }
    setSaving(true);
    setErr("");
    const payload = {
      ...form,
      keywords: form.keywords
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean),
      related_question_ids: form.related_question_ids
        .split(/[,\n]/)
        .map((k) => k.trim())
        .filter(Boolean),
    };
    try {
      if (editing === "new") await createHelpTerm(payload);
      else await updateHelpTerm(editing, payload);
      close();
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      await updateHelpTerm(t.id, { ...t, active: !t.active });
      await refresh();
    } catch { /* ignore */ }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.term}"?`)) return;
    try {
      await deleteHelpTerm(t.id);
      await refresh();
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="help-knowledge-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>
            PRIVA Knowledge Hub
          </h1>
          <p className="text-xs text-[#6B7280] mt-1">
            Shared AI/platform knowledge layer used by PRIVA across WarehouseX
            products — SAP / WMS glossary, definitions, and on-demand context
            surfaced inside SmartEvaluate, SmartImprove, and consultant flows.
          </p>
        </div>
        <Button
          data-testid="help-new-btn"
          onClick={openNew}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-9"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> New Term
        </Button>
      </div>

      {err && <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-600">{err}</div>}

      {/* Filter bar */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm px-3 py-2.5 mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
          <Input
            data-testid="help-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search term or keyword…"
            className="pl-8 h-8 text-xs rounded-sm border-[#E5E7EB]"
          />
        </div>
        <select
          data-testid="help-category-filter"
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          className="px-2.5 h-8 text-xs border border-[#E5E7EB] rounded-sm bg-white"
        >
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          data-testid="help-assessment-type-filter"
          value={atFilter}
          onChange={(e) => setAtFilter(e.target.value)}
          className="px-2.5 h-8 text-xs border border-[#E5E7EB] rounded-sm bg-white"
        >
          <option value="all">All assessment types</option>
          {ASSESSMENT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="ml-auto text-[11px] text-[#9CA3AF]">{filtered.length} / {items.length}</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px] w-48">Term</th>
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px] w-36">Assessment Type</th>
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px] w-32">Category</th>
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px]">Keywords</th>
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px] w-24">Status</th>
              <th className="text-right px-4 py-2.5 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#9CA3AF]">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#9CA3AF]">No terms match the current filters.</td></tr>
            ) : (
              filtered.map((t) => (
                <tr key={t.id} data-testid={`help-row-${t.id}`} className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC]">
                  <td className="px-4 py-3 font-semibold text-[#111827]">{t.term}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#E0F2FE] text-[#025979] border border-[#025979]/20">
                      {t.assessment_type || "Warehouse"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#374151]">{t.category}</td>
                  <td className="px-4 py-3 text-[#6B7280]">{(t.keywords || []).join(", ")}</td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`help-toggle-${t.id}`}
                      onClick={() => toggleActive(t)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${
                        t.active
                          ? "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0] hover:bg-[#D1FAE5]"
                          : "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB] hover:bg-[#E5E7EB]"
                      }`}
                    >
                      {t.active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        data-testid={`help-edit-${t.id}`}
                        onClick={() => openEdit(t)}
                        className="p-1 rounded-sm text-[#025979] hover:bg-[#E0F2FE]"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`help-delete-${t.id}`}
                        onClick={() => remove(t)}
                        className="p-1 rounded-sm text-[#DC2626] hover:bg-red-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="help-modal">
          <div className="bg-white w-full max-w-lg rounded-sm shadow-lg">
            <div className="flex items-center justify-between border-b border-[#E5E7EB] px-5 py-3">
              <h2 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>
                {editing === "new" ? "New Help Term" : "Edit Help Term"}
              </h2>
              <button onClick={close} className="text-[#6B7280] hover:text-[#111827]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="text-[11px] font-medium text-[#374151] block mb-1">Term *</label>
                <Input data-testid="help-form-term" value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} className="h-8 text-xs rounded-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-medium text-[#374151] block mb-1">Assessment Type *</label>
                  <select
                    data-testid="help-form-assessment-type"
                    value={form.assessment_type}
                    onChange={(e) => setForm({ ...form, assessment_type: e.target.value })}
                    className="w-full px-2 h-8 text-xs border border-[#E5E7EB] rounded-sm bg-white"
                  >
                    {ASSESSMENT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-medium text-[#374151] block mb-1">Category *</label>
                  <select
                    data-testid="help-form-category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-2 h-8 text-xs border border-[#E5E7EB] rounded-sm bg-white"
                  >
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#374151] block mb-1">
                  Related Question IDs
                  <span className="text-[#9CA3AF] font-normal ml-1">(question field_keys, comma-separated)</span>
                </label>
                <Input
                  data-testid="help-form-related-questions"
                  value={form.related_question_ids}
                  onChange={(e) => setForm({ ...form, related_question_ids: e.target.value })}
                  className="h-8 text-xs rounded-sm"
                  placeholder="cm_int_middleware_platform, cm_dev_enhancements_badis_count"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#374151] block mb-1">Active</label>
                <label className="flex items-center gap-2 h-8 text-xs text-[#374151]">
                  <input
                    data-testid="help-form-active"
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="w-3.5 h-3.5 accent-[#025979]"
                  />
                  Visible in the Help Assistant
                </label>
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#374151] block mb-1">Keywords (comma-separated)</label>
                <Input data-testid="help-form-keywords" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className="h-8 text-xs rounded-sm" placeholder="asn, advance shipping notice" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#374151] block mb-1">Definition *</label>
                <textarea
                  data-testid="help-form-definition"
                  value={form.definition}
                  onChange={(e) => setForm({ ...form, definition: e.target.value })}
                  rows={2}
                  className="w-full p-2 text-xs border border-[#E5E7EB] rounded-sm focus:outline-none focus:ring-1 focus:ring-[#025979]"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#374151] block mb-1">Why it matters</label>
                <textarea
                  data-testid="help-form-why"
                  value={form.why_it_matters}
                  onChange={(e) => setForm({ ...form, why_it_matters: e.target.value })}
                  rows={2}
                  className="w-full p-2 text-xs border border-[#E5E7EB] rounded-sm focus:outline-none focus:ring-1 focus:ring-[#025979]"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#374151] block mb-1">Example</label>
                <textarea
                  data-testid="help-form-example"
                  value={form.example}
                  onChange={(e) => setForm({ ...form, example: e.target.value })}
                  rows={2}
                  className="w-full p-2 text-xs border border-[#E5E7EB] rounded-sm focus:outline-none focus:ring-1 focus:ring-[#025979]"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-[#E5E7EB] px-5 py-3">
              <Button variant="outline" onClick={close} className="h-8 text-xs rounded-sm border-[#E5E7EB]">Cancel</Button>
              <Button
                data-testid="help-form-save"
                onClick={save}
                disabled={saving}
                className="h-8 text-xs bg-[#025979] hover:bg-[#03738C] text-white rounded-sm"
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
